/**
 * DisplayHintControls — shown when a clue cell is focused.
 * Allows adding line breaks and hyphens at specific character positions.
 *
 * Layout (top to bottom):
 *   Row of line-break buttons (one between each character pair)
 *   Row of character tiles spelling out the clue
 *   Row of hyphen buttons (one between each character pair)
 */

import React from 'react';
import type { PuzzleJSON, DisplayHints } from '../../types';
import type { EditorActions } from './useEditorState';

interface DisplayHintControlsProps {
  puzzle: PuzzleJSON;
  actions: EditorActions;
  focusedCell: { col: number; row: number } | null;
}

// Tile and button dimensions
const TILE_SIZE = 42;
const TILE_GAP = 3;
const BTN_W = TILE_SIZE;
const BTN_H = 28;

const DisplayHintControls: React.FC<DisplayHintControlsProps> = ({
  puzzle, actions, focusedCell,
}) => {
  // Derive cell info if a cell is focused and has text
  let cellInfo: { col: number; row: number; text: string; key: string; hints: DisplayHints | undefined } | null = null;
  if (focusedCell) {
    const { col, row } = focusedCell;
    const pairIdx = Math.floor(row / 2);
    const slotIdx = (row % 2) as 0 | 1;
    const text = puzzle.columns[col]?.pairs[pairIdx]?.clues[slotIdx] ?? '';
    if (text) {
      const key = `${col}-${row}`;
      cellInfo = { col, row, text, key, hints: puzzle.displayHints?.clues?.[key] };
    }
  }

  const breaks = new Set(cellInfo?.hints?.lineBreaks ?? []);
  const hyphens = new Set(cellInfo?.hints?.hyphens ?? []);

  const toggleBreak = (pos: number) => {
    if (!cellInfo) return;
    const newBreaks = breaks.has(pos)
      ? [...breaks].filter(b => b !== pos)
      : [...breaks, pos].sort((a, b) => a - b);
    const newHyphens = [...hyphens];
    const newHints: DisplayHints | null =
      newBreaks.length === 0 && newHyphens.length === 0
        ? null
        : { lineBreaks: newBreaks.length > 0 ? newBreaks : undefined, hyphens: newHyphens.length > 0 ? newHyphens : undefined };
    actions.setDisplayHint('clues', cellInfo.key, newHints);
  };

  const toggleHyphen = (pos: number) => {
    if (!cellInfo) return;
    const newHyphens = hyphens.has(pos)
      ? [...hyphens].filter(h => h !== pos)
      : [...hyphens, pos].sort((a, b) => a - b);
    const newBreaks = [...breaks];
    const newHints: DisplayHints | null =
      newBreaks.length === 0 && newHyphens.length === 0
        ? null
        : { lineBreaks: newBreaks.length > 0 ? newBreaks : undefined, hyphens: newHyphens.length > 0 ? newHyphens : undefined };
    actions.setDisplayHint('clues', cellInfo.key, newHints);
  };

  const clearAll = () => {
    if (!cellInfo) return;
    actions.setDisplayHint('clues', cellInfo.key, null);
  };

  return (
    <div style={{
      fontFamily: 'Inter, sans-serif', color: '#f7e5c5',
      overflow: 'hidden',
      height: 139,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: cellInfo ? 10 : 0,
      }}>
        <span style={{
          fontFamily: 'Inter, sans-serif', fontSize: 16, fontWeight: 600,
          color: '#c8b8a0', textTransform: 'uppercase' as const,
          letterSpacing: 1,
        }}>
          Line Breaks
        </span>
        <span style={{ opacity: 0.6, fontSize: 12 }}>
          {cellInfo
            ? <>Col {cellInfo.col + 1}, Row {cellInfo.row + 1}: <strong>"{cellInfo.text.toUpperCase()}"</strong></>
            : '— select a clue to edit'
          }
        </span>
        {cellInfo && (breaks.size > 0 || hyphens.size > 0) && (
          <button
            onClick={clearAll}
            style={{
              background: '#514b44', border: 'none', borderRadius: 4,
              color: '#f7e5c5', fontSize: 11, padding: '4px 10px', cursor: 'pointer',
              fontFamily: 'Inter, sans-serif', fontWeight: 600,
            }}
          >
            Clear All
          </button>
        )}
      </div>

      {cellInfo && (() => {
        const chars = cellInfo.text.split('');
        const step = TILE_SIZE + TILE_GAP;
        const buttonSlots = chars.length > 1 ? chars.length - 1 : 0;

        return (
          <div style={{ position: 'relative', width: chars.length * step - TILE_GAP }}>

            {/* Top row: line break buttons — between each pair of characters */}
            <div style={{ height: BTN_H, position: 'relative', marginBottom: TILE_GAP }}>
              {Array.from({ length: buttonSlots }, (_, i) => {
                const active = breaks.has(i);
                const cx = (i + 1) * step - TILE_GAP - BTN_W / 2;
                return (
                  <button
                    key={i}
                    onClick={() => toggleBreak(i)}
                    title={`${active ? 'Remove' : 'Add'} line break after "${chars[i].toUpperCase()}"`}
                    style={{
                      position: 'absolute',
                      left: cx,
                      top: 0,
                      width: BTN_W,
                      height: BTN_H,
                      border: 'none',
                      borderRadius: 5,
                      background: active ? '#e6b832' : '#3a3630',
                      color: active ? '#262626' : '#f7e5c5',
                      fontSize: 16,
                      fontWeight: 700,
                      cursor: 'pointer',
                      padding: 0,
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    ↵
                  </button>
                );
              })}
            </div>

            {/* Middle row: character tiles */}
            <div style={{ display: 'flex', gap: TILE_GAP }}>
              {chars.map((char, i) => (
                <span
                  key={i}
                  style={{
                    width: TILE_SIZE,
                    height: TILE_SIZE,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#514b44',
                    borderRadius: 5,
                    fontSize: 22,
                    fontWeight: 700,
                    borderLeft: breaks.has(i - 1) ? '3px solid #e6b832' : undefined,
                    position: 'relative',
                  }}
                >
                  {char.toUpperCase()}
                  {hyphens.has(i) && (
                    <span style={{
                      position: 'absolute',
                      right: -6,
                      color: '#1f7d7e',
                      fontSize: 22,
                      fontWeight: 800,
                    }}>
                      -
                    </span>
                  )}
                </span>
              ))}
            </div>

            {/* Bottom row: hyphen buttons — between each pair of characters */}
            <div style={{ height: BTN_H, position: 'relative', marginTop: TILE_GAP }}>
              {Array.from({ length: buttonSlots }, (_, i) => {
                const active = hyphens.has(i);
                const cx = (i + 1) * step - TILE_GAP - BTN_W / 2;
                return (
                  <button
                    key={i}
                    onClick={() => toggleHyphen(i)}
                    title={`${active ? 'Remove' : 'Add'} hyphen after "${chars[i].toUpperCase()}"`}
                    style={{
                      position: 'absolute',
                      left: cx,
                      top: 0,
                      width: BTN_W,
                      height: BTN_H,
                      border: 'none',
                      borderRadius: 5,
                      background: active ? '#1f7d7e' : '#3a3630',
                      color: active ? '#f7e5c5' : '#f7e5c5',
                      fontSize: 18,
                      fontWeight: 800,
                      cursor: 'pointer',
                      padding: 0,
                      fontFamily: 'Inter, sans-serif',
                    }}
                  >
                    -
                  </button>
                );
              })}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default React.memo(DisplayHintControls);
